import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";

interface SponsorshipClientInfoModalProps {
  open: boolean;
  onClose: () => void;
  whatsappNumber: string;
  onNumberChange: (value: string) => void;
  fullName: string;
  onFullNameChange: (value: string) => void;
  onSubmit: () => void;
  primaryColor?: string;
  secondaryColor?: string;
}

const SponsorshipClientInfoModal: React.FC<SponsorshipClientInfoModalProps> = ({
  open,
  onClose,
  whatsappNumber,
  onNumberChange,
  fullName,
  onFullNameChange,
  onSubmit,
  primaryColor = "#1976d2",
  secondaryColor = "#f5f5f5",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: primaryColor,
          color: "white",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h4" fontWeight="bold">
            Apply for Sponsorship
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, mt: 1, color: "rgba(255,255,255,0.8)" }}
        >
          Enter your contact details to proceed with your sponsorship
          application
        </Typography>
      </Box>
      <DialogContent sx={{ p: 3, mt: 1 }}>
        <TextField
          fullWidth
          label="Enter Full Name*"
          placeholder="e.g. John Doe"
          value={fullName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFullNameChange(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />

        <TextField
          fullWidth
          label="WhatsApp Number"
          placeholder="+263 77 123 4567"
          value={whatsappNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onNumberChange(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />

        <Typography variant="caption" sx={{ color: "text.secondary", mb: 4 }}>
          We will contact you via WhatsApp regarding your sponsorship
          application
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: "text.primary",
              borderColor: "divider",
              boxShadow: 0,
              backgroundColor: secondaryColor,
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onSubmit}
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: primaryColor,
              color: "white",
              "&:hover": {
                background: primaryColor,
                opacity: 0.9,
              },
            }}
          >
            Continue
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SponsorshipClientInfoModal;
